import { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { 
  ArrowUp, ArrowDown, AlertTriangle, Link2, Check, 
  Footprints, Timer, ChevronDown, ChevronUp, ChevronLeft, ChevronRight 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { RestTimer } from '../components/RestTimer';
import { toast } from 'sonner';
import { 
  getSettings, 
  getWorkoutsByDate, 
  saveWorkoutLog, 
  getAllWorkoutLogs 
} from '../lib/db';
import { 
  WORKOUT_TEMPLATE, 
  getDayType, 
  getDateString, 
  formatDate 
} from '../lib/constants';

export const Training = () => {
  const [settings, setSettings] = useState(null);
  const [dayType, setDayType] = useState(null);
  const [workout, setWorkout] = useState(null);
  const [previousWorkout, setPreviousWorkout] = useState(null);
  const [walkCompleted, setWalkCompleted] = useState(false);
  const [activeExercise, setActiveExercise] = useState(null);
  const [showTimer, setShowTimer] = useState(false);
  const [timerDuration, setTimerDuration] = useState(90);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const today = getDateString(selectedDate);
  const isToday = getDateString(selectedDate) === getDateString(new Date());

  const navigateDay = (delta) => {
    setSelectedDate(prev => {
      const next = new Date(prev);
      next.setDate(next.getDate() + delta);
      return next;
    });
  };

  const loadData = useCallback(async () => {
    try {
      const settingsData = await getSettings();
      setSettings(settingsData);
      
      const type = getDayType(today, settingsData.startDate);
      setDayType(type);

      // Load today's workout if exists
      const todayWorkouts = await getWorkoutsByDate(today);
      
      if (type === 'gym' || type === 'cheat') {
        if (todayWorkouts.length > 0 && todayWorkouts[0].type === 'gym') {
          setWorkout(todayWorkouts[0]);
        } else {
          // Initialize new workout
          const newWorkout = {
            id: uuidv4(),
            date: today,
            type: 'gym',
            completed: false,
            exercises: WORKOUT_TEMPLATE.map(ex => ({
              ...ex,
              sets: Array(ex.sets).fill(null).map((_, i) => ({
                setNum: i + 1,
                weight: '',
                reps: '',
                completed: false,
              })),
            })),
          };
          setWorkout(newWorkout);
        }

        // Find previous gym workout for ghost text
        const allWorkouts = await getAllWorkoutLogs();
        const prevGym = allWorkouts
          .filter(w => w.type === 'gym' && w.date !== today && w.completed)
          .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
        setPreviousWorkout(prevGym);
      } else {
        // Walk day
        if (todayWorkouts.length > 0 && todayWorkouts[0].type === 'walk') {
          setWalkCompleted(todayWorkouts[0].completed);
          setWorkout(todayWorkouts[0]);
        }
      }
    } catch (error) {
      console.error('Error loading training data:', error);
    } finally {
      setLoading(false);
    }
  }, [today]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setLoading(true);
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const updateSet = (exerciseId, setIndex, field, value) => {
    setWorkout(prev => ({
      ...prev,
      exercises: prev.exercises.map(ex => 
        ex.id === exerciseId 
          ? {
              ...ex,
              sets: ex.sets.map((s, i) => 
                i === setIndex ? { ...s, [field]: value } : s
              ),
            }
          : ex
      ),
    }));
  };

  const completeSet = (exerciseId, setIndex) => {
    const exercise = workout.exercises.find(ex => ex.id === exerciseId);
    const set = exercise.sets[setIndex];
    
    if (!set.weight || !set.reps) {
      toast.error('Enter weight and reps first');
      return;
    }

    updateSet(exerciseId, setIndex, 'completed', true);
    setTimerDuration(exercise.type === 'isolation' ? 60 : 90);
    setShowTimer(true);
    toast.success('Set completed!');
  };

  const getPreviousSetData = (exerciseId, setIndex) => {
    if (!previousWorkout) return null;
    const prevExercise = previousWorkout.exercises.find(ex => ex.id === exerciseId);
    if (!prevExercise) return null;
    return prevExercise.sets[setIndex];
  };

  const getComparison = (current, previous) => {
    if (!previous || !current) return null;
    const currentWeight = parseFloat(current);
    const prevWeight = parseFloat(previous);
    if (isNaN(currentWeight) || isNaN(prevWeight)) return null;
    
    if (currentWeight > prevWeight) return 'up';
    if (currentWeight < prevWeight) return 'down';
    return 'same';
  };

  const saveWalk = async () => {
    const walkWorkout = {
      id: workout?.id || uuidv4(),
      date: today,
      type: 'walk',
      completed: true,
    };
    
    await saveWorkoutLog(walkWorkout);
    setWorkout(walkWorkout);
    setWalkCompleted(true);
    toast.success('Walk completed! 🚶');
  };

  const completeWorkout = async () => {
    const allSetsCompleted = workout.exercises.every(ex => 
      ex.sets.every(s => s.completed)
    );

    if (!allSetsCompleted) {
      toast.error('Complete all sets first');
      return;
    }

    const completed = { ...workout, completed: true };
    await saveWorkoutLog(completed);
    setWorkout(completed);
    toast.success('Workout complete! 💪');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isWalkDay = dayType === 'walk';

  return (
    <div className="space-y-6" data-testid="training-page">
      {/* Header with Day Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigateDay(-1)}
          className="h-10 w-10 rounded-xl hover:bg-white/10"
        >
          <ChevronLeft size={20} />
        </Button>
        <div className="text-center flex-1">
          <p className="text-xs font-bold uppercase tracking-widest text-gray-500">
            {isToday ? 'Today' : formatDate(today)}
          </p>
          <h1 className="text-2xl font-black tracking-tight">
            {isWalkDay ? 'Walk Day' : 'Full Body Workout'}
          </h1>
          {!isToday && (
            <button 
              onClick={() => setSelectedDate(new Date())}
              className="text-[10px] text-[#00E676] font-bold uppercase tracking-wider mt-1"
            >
              ← Back to Today
            </button>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigateDay(1)}
          className="h-10 w-10 rounded-xl hover:bg-white/10"
        >
          <ChevronRight size={20} />
        </Button>
      </div>
      {!isWalkDay && workout?.completed && (
        <div className="flex items-center justify-center gap-2 px-3 py-1.5 bg-[#00E676]/20 rounded-full w-fit mx-auto">
          <Check size={16} className="text-[#00E676]" />
          <span className="text-xs font-bold text-[#00E676]">DONE</span>
        </div>
      )}

      {/* Rest Timer */}
      {showTimer && !isWalkDay && (
        <RestTimer 
          initialTime={timerDuration} 
          onComplete={() => setShowTimer(false)} 
        />
      )}

      {isWalkDay ? (
        /* Walk Day UI - Simple toggle */
        <div 
          className={`bg-[#24243e] border rounded-2xl p-6 ${
            walkCompleted ? 'border-[#00E676]/30' : 'border-white/5'
          }`} 
          data-testid="walk-form"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                walkCompleted ? 'bg-[#00E676]/20' : 'bg-blue-500/20'
              }`}>
                {walkCompleted ? (
                  <Check className="text-[#00E676]" size={28} />
                ) : (
                  <Footprints className="text-blue-400" size={28} />
                )}
              </div>
              <div>
                <h2 className="font-bold text-lg">Daily Walk</h2>
                <p className="text-sm text-gray-400">
                  {walkCompleted ? 'Completed today!' : 'Tap to mark as done'}
                </p>
              </div>
            </div>
            
            {!walkCompleted ? (
              <Button 
                onClick={saveWalk}
                className="h-14 px-6 bg-[#00E676] text-black font-black uppercase tracking-wide hover:bg-[#00c853]"
                data-testid="save-walk-btn"
              >
                Done
              </Button>
            ) : (
              <div className="text-right">
                <p className="text-2xl font-black text-[#00E676]">✓</p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Complete</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Gym Day UI */
        <div className="space-y-4">
          {/* Group exercises by superset */}
          {WORKOUT_TEMPLATE.reduce((acc, exercise, index) => {
            // Skip if already added as part of superset
            if (acc.processed.includes(exercise.id)) return acc;

            const supersetPartner = exercise.supersetWith 
              ? WORKOUT_TEMPLATE.find(e => e.id === exercise.supersetWith)
              : null;

            if (supersetPartner) {
              acc.processed.push(exercise.id, supersetPartner.id);
              acc.elements.push(
                <SupersetGroup 
                  key={`superset-${exercise.id}`}
                  exercises={[exercise, supersetPartner]}
                  workout={workout}
                  previousWorkout={previousWorkout}
                  activeExercise={activeExercise}
                  setActiveExercise={setActiveExercise}
                  updateSet={updateSet}
                  completeSet={completeSet}
                  getPreviousSetData={getPreviousSetData}
                  getComparison={getComparison}
                />
              );
            } else {
              acc.processed.push(exercise.id);
              acc.elements.push(
                <ExerciseCard
                  key={exercise.id}
                  exercise={exercise}
                  workout={workout}
                  previousWorkout={previousWorkout}
                  activeExercise={activeExercise}
                  setActiveExercise={setActiveExercise}
                  updateSet={updateSet}
                  completeSet={completeSet}
                  getPreviousSetData={getPreviousSetData}
                  getComparison={getComparison}
                />
              );
            }
            return acc;
          }, { processed: [], elements: [] }).elements}

          {/* Complete Workout Button */}
          {!workout?.completed && (
            <Button 
              onClick={completeWorkout}
              className="w-full bg-[#00E676] text-black font-black uppercase tracking-wide hover:bg-[#00c853] h-14"
              data-testid="complete-workout-btn"
            >
              Complete Workout
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

const SupersetGroup = ({ exercises, workout, previousWorkout, activeExercise, setActiveExercise, updateSet, completeSet, getPreviousSetData, getComparison }) => {
  return (
    <div className="relative" data-testid="superset-group">
      {/* Superset indicator */}
      <div className="absolute -left-2 top-4 bottom-4 w-1 bg-[#00E676]/30 rounded-full" />
      <div className="absolute -left-4 top-1/2 -translate-y-1/2 flex items-center">
        <div className="w-6 h-6 rounded-full bg-[#00E676]/20 flex items-center justify-center">
          <Link2 size={12} className="text-[#00E676]" />
        </div>
      </div>
      
      <div className="space-y-3 ml-4">
        {exercises.map(exercise => (
          <ExerciseCard
            key={exercise.id}
            exercise={exercise}
            workout={workout}
            previousWorkout={previousWorkout}
            activeExercise={activeExercise}
            setActiveExercise={setActiveExercise}
            updateSet={updateSet}
            completeSet={completeSet}
            getPreviousSetData={getPreviousSetData}
            getComparison={getComparison}
            isSuperset
          />
        ))}
      </div>
    </div>
  );
};

const ExerciseCard = ({ 
  exercise, 
  workout, 
  activeExercise, 
  setActiveExercise, 
  updateSet, 
  completeSet, 
  getPreviousSetData, 
  getComparison,
  isSuperset 
}) => {
  const workoutExercise = workout?.exercises.find(e => e.id === exercise.id);
  const isExpanded = activeExercise === exercise.id;
  const allSetsComplete = workoutExercise?.sets.every(s => s.completed);

  return (
    <div 
      className={`bg-[#24243e] border rounded-xl overflow-hidden transition-all ${
        allSetsComplete 
          ? 'border-[#00E676]/30' 
          : 'border-white/5'
      }`}
      data-testid={`exercise-${exercise.id}`}
    >
      {/* Header */}
      <button
        onClick={() => setActiveExercise(isExpanded ? null : exercise.id)}
        className="w-full flex items-center justify-between p-4 active:bg-white/5"
      >
        <div className="flex items-center gap-3">
          {allSetsComplete && (
            <div className="w-6 h-6 rounded-full bg-[#00E676] flex items-center justify-center">
              <Check size={14} className="text-black" strokeWidth={3} />
            </div>
          )}
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="font-bold">{exercise.name}</span>
              {exercise.warning && (
                <AlertTriangle size={14} className="text-yellow-500" />
              )}
            </div>
            <span className="text-xs text-gray-500">
              {exercise.sets}×{exercise.reps}
            </span>
          </div>
        </div>
        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>

      {/* Warning note */}
      {exercise.notes && isExpanded && (
        <div className="px-4 pb-2">
          <div className="flex items-center gap-2 px-3 py-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <AlertTriangle size={14} className="text-yellow-500" />
            <span className="text-xs text-yellow-400">{exercise.notes}</span>
          </div>
        </div>
      )}

      {/* Sets */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-2">
          {workoutExercise?.sets.map((set, index) => {
            const prevSet = getPreviousSetData(exercise.id, index);
            const weightComparison = getComparison(set.weight, prevSet?.weight);

            return (
              <div 
                key={index}
                className={`flex items-center gap-2 p-2 rounded-lg ${
                  set.completed 
                    ? 'bg-[#00E676]/10' 
                    : 'bg-[#161625]'
                }`}
                data-testid={`set-${exercise.id}-${index}`}
              >
                <span className="w-8 text-center text-sm font-bold text-gray-500">
                  {set.setNum}
                </span>
                
                <div className="flex-1 relative">
                  <Input
                    type="number"
                    inputMode="decimal"
                    placeholder={prevSet?.weight || 'kg'}
                    value={set.weight}
                    onChange={(e) => updateSet(exercise.id, index, 'weight', e.target.value)}
                    disabled={set.completed}
                    className="bg-transparent border-white/10 text-center font-mono pr-8"
                    data-testid={`weight-input-${exercise.id}-${index}`}
                  />
                  {weightComparison && set.weight && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      {weightComparison === 'up' && <ArrowUp size={14} className="text-[#00E676]" />}
                      {weightComparison === 'down' && <ArrowDown size={14} className="text-red-500" />}
                    </div>
                  )}
                </div>
                
                <span className="text-gray-500">×</span>
                
                <Input
                  type="number"
                  inputMode="numeric"
                  placeholder={prevSet?.reps || 'reps'}
                  value={set.reps}
                  onChange={(e) => updateSet(exercise.id, index, 'reps', e.target.value)}
                  disabled={set.completed}
                  className="w-20 bg-transparent border-white/10 text-center font-mono"
                  data-testid={`reps-input-${exercise.id}-${index}`}
                />
                
                <Button
                  size="icon"
                  variant={set.completed ? 'default' : 'outline'}
                  onClick={() => !set.completed && completeSet(exercise.id, index)}
                  disabled={set.completed}
                  className={`h-10 w-10 ${
                    set.completed 
                      ? 'bg-[#00E676] text-black hover:bg-[#00E676]' 
                      : 'border-white/10 hover:bg-white/5'
                  }`}
                  data-testid={`complete-set-${exercise.id}-${index}`}
                >
                  <Check size={16} strokeWidth={3} />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Training;
